/**
 * The two rules that lapse this year, and the commissioners' right to tidy how they read.
 *
 * A provisional rule goes back on the agenda whether anybody proposes it or not, so unlike every
 * other docket item it has no author sitting in the app to fix its wording. It arrives as a row of
 * `rulebook.json`, generated from the 2025 proposal document, and it reads like what it is: a
 * heading somebody typed into a Google Doc at midnight, with the vote result shouted into the
 * middle of it.
 *
 *   "Expand Farm System Draft To Two Rounds (proposed by Rajan) – CONDITIONAL PASS, MANDATORY
 *    REVOTE IN 2026"
 *
 * followed by a summary that opens by saying the same thing again. On a phone that is four lines
 * of banner before the reader reaches a single word about what the rule DOES.
 *
 * TWO SEPARATE FIXES LIVE HERE, and it matters that they are separate.
 *
 * `splitRuleTitle` is presentation. The proposer and the vote result are facts, they are simply
 * not part of the rule's NAME — so they are lifted out and shown as a chip and a byline, and
 * nothing is lost or edited. It applies to every provisional rule automatically and needs no
 * commissioner to have done anything.
 *
 * `revoteWording` is an edit, and edits are recorded. 2019-I-2 §2 lets the commissioners tidy the
 * wording of anything going to a vote; 2020-I-1 §2 gives the original proposer a veto over a
 * change he does not accept. Both of those are only meaningful if the change is visible, so this
 * is stored as an `Override` — the mechanism this app already uses for "a commissioner set this
 * by hand, against what the rules say" — which means it carries who, when, what it said before,
 * and why, and can be put back. A silent edit to a rule's text on its way to a vote is exactly the
 * thing the citation chips everywhere else exist to prevent.
 */

import type { RulebookEntry } from './rulebook.js';
import { inForce, type Override } from './overrides.js';

/** `revote:<rule id>:title`. One target per field, because `inForce` is keyed by target. */
export const revoteTarget = (id: string, field: 'title' | 'summary') => `revote:${id}:${field}`;

export interface SplitTitle {
  /** The rule's actual name, with the byline and the vote banner taken out. */
  title: string;
  /** "Rajan", where the source title named him. */
  proposer?: string;
  /** True when the source title carried the conditional-pass banner. */
  banner: boolean;
}

/*
 * The source documents use an en-dash, an em-dash and a hyphen in the same file, spell it both
 * "REVOTE" and "RE-VOTE", and capitalise "proposed" either way. Matching all of it here is less
 * fragile than normalising `rulebook.json`, which is generated and would lose the edit on the next
 * regeneration.
 */
/*
 * The vote RESULT, wherever the generated title tacked it on. It started as conditional-pass only,
 * which was all the re-vote card needed; the by-year view reads every title in the book and turned
 * up "… (Proposed by Rajan) – PASSED" as well. A result is a fact about a rule, not part of its
 * name, and it is already a chip on every screen that shows one.
 *
 * Checked against all 141 titles in the rulebook: four match, all four are 2025 proposals, and
 * nothing legitimate is eaten. The words are required in caps after a dash for that reason - a
 * lower-case "passed" mid-title is prose, not a banner.
 */
const BANNER = /\s*[–—-]\s*(?:CONDITIONAL\s+PASS|PASSED|FAILED|RESCINDED|WITHDRAWN)\b.*$/;
const BYLINE = /\s*\(\s*propos(?:ed|al)\s+by\s+([^)]+?)\s*\)/i;
const SUMMARY_BANNER = /^\s*CONDITIONAL\s+PASS\s*[–—-]\s*MANDATORY\s+RE-?VOTE\s+IN\s+\d{4}\.\s*/i;

export function splitRuleTitle(raw: string): SplitTitle {
  const banner = BANNER.test(raw);
  let title = raw.replace(BANNER, '');
  const by = BYLINE.exec(title);
  if (by) title = title.replace(BYLINE, '');
  return {
    title: title.trim(),
    ...(by?.[1] ? { proposer: by[1].trim() } : {}),
    banner,
  };
}

/** The summary without the sentence the chip already says. */
export const stripSummaryBanner = (raw: string) => raw.replace(SUMMARY_BANNER, '').trim();

export interface RevoteWording {
  title: string;
  summary: string;
  proposer?: string;
  /** What the rulebook says, kept so an edited row can show what it used to say. */
  original: { title: string; summary: string };
  edited: boolean;
  editedBy?: string;
  editedAt?: string;
  reason?: string;
  /** The override rows behind the edit, for the revert button. */
  applied: Override[];
}

/**
 * How a provisional rule should read right now: the rulebook, tidied, plus any commissioner edit.
 *
 * Note the order. The banner and byline are stripped FIRST, so a commissioner opening the editor
 * sees the clean title rather than being handed the mess to delete by hand — and so an edit that
 * changes nothing else does not silently reinstate the banner.
 */
export function revoteWording(rule: RulebookEntry, all: readonly Override[]): RevoteWording {
  const split = splitRuleTitle(rule.title);
  const original = { title: split.title, summary: stripSummaryBanner(rule.summary) };

  const live = inForce(all);
  const t = live.find((o) => o.target === revoteTarget(rule.id, 'title'));
  const s = live.find((o) => o.target === revoteTarget(rule.id, 'summary'));
  const applied = [t, s].filter(Boolean) as Override[];
  const newest = applied.slice().sort((a, b) => b.at.localeCompare(a.at))[0];

  return {
    title: t?.setTo ?? original.title,
    summary: s?.setTo ?? original.summary,
    ...(split.proposer ? { proposer: split.proposer } : {}),
    original,
    edited: applied.length > 0,
    ...(newest ? { editedBy: newest.by, editedAt: newest.at, reason: newest.reason } : {}),
    applied,
  };
}

/**
 * Is this override a re-vote wording rather than a league value?
 *
 * The Proposals page is where these are made and where they are read; the admin's Set by hand
 * table is for values the league runs on, and a tidied docket wording is not one (Tim, 4
 * September). Once the room has voted, the wording it tidied is the wording that was voted on and
 * the rule has joined the rulebook through `effectiveRules`; the row is history the proposal
 * record already carries.
 */
export const isRevoteWording = (o: Pick<Override, 'target'>): boolean => o.target.startsWith('revote:');
